"use client"

import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { connect, useDispatch, useSelector } from "react-redux"
import { Mobile, PC, Tablet, Notebook } from "../../../views/ReactiveMode"
import {
    FaArrowLeft,
    FaSave,
    FaImage,
    FaPlus,
    FaTrash,
    FaUsers,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaPhone,
    FaEnvelope,
} from "react-icons/fa"
import actionTypes from "../../../constants/actionTypes"
import DOMPurify from "dompurify"

// 동아리 전용 메뉴 ID
const CLUBS_MENU_ID = 3

// 텍스트 안전하게 처리하는 헬퍼 함수 수정
const safeText = (text) => {
    if (!text) return ""

    // HTML 태그 제거 후 줄바꿈만 <br>로 변환
    const sanitized = DOMPurify.sanitize(text, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        ALLOW_DATA_ATTR: false,
        FORBID_SCRIPT: true,
    })

    return sanitized.replace(/\n/g, "<br>")
}

// HTML 템플릿 생성 함수
const generateClubHTML = (clubData) => {
    const {
        name,
        description,
        longDescription,
        imageUrl,
        galleryImages,
        members,
        leader,
        contact,
        email,
        meetingTime,
        meetingPlace,
        activities,
    } = clubData

    // 모든 텍스트 입력값 sanitize
    const safeName = safeText(name)
    const safeDescription = safeText(description)
    const safeLongDescription = safeText(longDescription)
    const safeLeader = safeText(leader)
    const safeContact = safeText(contact)
    const safeMeetingTime = safeText(meetingTime)
    const safeMeetingPlace = safeText(meetingPlace)

    // 이미지 URL도 검증
    const safeImageUrl = imageUrl && /^data:image\/[a-zA-Z]*;base64,/.test(imageUrl) ? imageUrl : ""

    // 갤러리 이미지도 검증
    const safeGalleryImages = galleryImages.filter((img) => img && /^data:image\/[a-zA-Z]*;base64,/.test(img))

    // 갤러리 이미지 HTML 생성 (검증된 이미지만)
    const galleryHTML =
        safeGalleryImages.length > 0
            ? safeGalleryImages.map((img) => `<img src="${img}" alt="갤러리 이미지" class="gallery-image" style="height: 100%"/>`).join("")
            : ""

    // 활동 내용 HTML 생성 (각 활동 내용도 sanitize)
    const activitiesHTML =
        activities && activities.length > 0
            ? activities
                .filter((activity) => activity.trim())
                .map((activity) => `<li>${safeText(activity)}</li>`)
                .join("")
            : ""

    return `
    <div class="club-detail-container">
      <header class="club-header">
        <div class="club-main-info">
          ${safeImageUrl ? `<div class="club-image"><img src="${safeImageUrl}" alt="${safeName} 대표 이미지" /></div>` : ""}
          <div class="club-info">
            <h1 class="club-name">${safeName}</h1>
            <p class="club-description">${safeDescription}</p>
          </div>
        </div>
      </header>

      <main class="club-content">
        ${
        safeLongDescription
            ? `
        <section class="club-section">
          <h2>상세 설명</h2>
          <div class="club-long-description">
            ${safeLongDescription}
          </div>
        </section>
        `
            : ""
    }

        <section class="club-section">
          <h2>동아리 정보</h2>
          <div class="club-details-grid">
            <div class="detail-item">
              <span class="detail-label"><i class="icon-users"></i> 회원 수</span>
              <span class="detail-value">${Math.max(0, Number.parseInt(members) || 0)}명</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="icon-user"></i> 리더</span>
              <span class="detail-value">${safeLeader}</span>
            </div>
    
            ${
        safeMeetingTime
            ? `
            <div class="detail-item">
              <span class="detail-label"><i class="icon-calendar"></i> 모임 시간</span>
              <span class="detail-value">${safeMeetingTime}</span>
            </div>
            `
            : ""
    }
            ${
        safeMeetingPlace
            ? `
            <div class="detail-item">
              <span class="detail-label"><i class="icon-location"></i> 모임 장소</span>
              <span class="detail-value">${safeMeetingPlace}</span>
            </div>
            `
            : ""
    }
          </div>
        </section>

        ${
        activitiesHTML
            ? `
        <section class="club-section">
          <h2>주요 활동</h2>
          <ul class="activities-list">
            ${activitiesHTML}
          </ul>
        </section>
        `
            : ""
    }

        ${
        galleryHTML
            ? `
        <section class="club-section">
          <h2>갤러리</h2>
          <div class="gallery-grid">
            ${galleryHTML}
          </div>
        </section>
        `
            : ""
    }
      </main>

      <style>
        .club-detail-container {
          max-width: 100%;
          width: 100%;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Noto Sans KR', sans-serif;
          line-height: 1.6;
          color: #333;
        }

        .club-header {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #f0f0f0;
        }

        .club-main-info {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .club-image img {
          width: 200px;
          height: 150px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .club-info {
          flex: 1;
        }

        .club-name {
          font-size: 2.5em;
          font-weight: bold;
          margin: 0 0 10px 0;
          color: #2c3e50;
        }

        .club-description {
          font-size: 1.1em;
          color: #555;
          margin: 0;
        }

        .club-section {
          margin-bottom: 30px;
        }

        .club-section h2 {
          font-size: 1.5em;
          color: #2c3e50;
          margin-bottom: 15px;
          padding-bottom: 8px;
          border-bottom: 1px solid #eee;
        }

        .club-long-description {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #3498db;
        }

        .club-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .detail-label {
          font-weight: 600;
          color: #495057;
        }

        .detail-value {
          color: #2c3e50;
          font-weight: 500;
        }

        .activities-list {
          list-style: none;
          padding: 0;
        }

        .activities-list li {
          background: #fff;
          margin-bottom: 8px;
          padding: 12px 16px;
          border-left: 4px solid #27ae60;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border-radius: 0 4px 4px 0;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 15px;
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .gallery-image:hover {
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .club-main-info {
            flex-direction: column;
          }
          
          .club-image img {
            width: 100%;
            height: 100%;
          }
          
          .club-details-grid {
            grid-template-columns: 1fr;
          }
          
          .detail-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
          }
        }
      </style>
    </div>
  `
}

function ClubWrite() {
    const { id } = useParams()
    const isEdit = Boolean(id)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Redux 상태
    const { detail, write } = useSelector((state) => state.board)
    const loginData = useSelector((state) => state.login)

    // 권한 체크 상태
    const [hasPermission, setHasPermission] = useState(false)
    const [permissionChecked, setPermissionChecked] = useState(false)

    // 폼 상태
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        longDescription: "",
        imageUrl: "",
        galleryImages: [],
        members: 0,
        leader: "",
        contact: "",
        email: "",
        meetingTime: "",
        meetingPlace: "",
        activities: [""],
    })

    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)


    // 관리자 권한 체크 함수
    const checkAdminPermission = useCallback(() => {
        const { isLogin, role } = loginData


        if (!isLogin || !role) {
            return false
        }

        const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN"]
        const hasPermission = adminRoles.includes(role)

        return hasPermission
    }, [loginData])

    // 컴포넌트 마운트 시 권한 체크
    useEffect(() => {
        const permission = checkAdminPermission()
        setHasPermission(permission)
        setPermissionChecked(true)

        if (!permission) {
            alert("동아리 등록/수정 권한이 없습니다.")
            navigate("/clubs", { replace: true })
            return
        }
    }, [checkAdminPermission, navigate])

    // 수정 모드일 때 기존 데이터 로드
    useEffect(() => {
        if (isEdit && id && hasPermission) {
            dispatch({
                type: actionTypes.BOARD_DETAIL_FETCH,
                payload: { id },
            })
        }
    }, [dispatch, isEdit, id, hasPermission])

    // 기존 데이터를 폼에 설정 (HTML에서 데이터 추출)
    useEffect(() => {
        if (isEdit && detail.data && hasPermission) {
            try {
                // HTML 내용에서 데이터 추출하거나 JSON 파싱 시도
                let clubData = {}

                // 먼저 JSON 파싱 시도 (기존 데이터 호환성)
                try {
                    // HTML 형식인지 확인
                    if (detail.data.contents && detail.data.contents.trim().startsWith("<")) {
                        clubData = {
                            description: "",
                            members: 0,
                        }
                    } else {
                        clubData = JSON.parse(detail.data.contents || "{}")
                    }
                } catch {
                    // HTML에서 데이터 추출 (향후 구현 가능)
                    clubData = {}
                }

                setFormData({
                    name: detail.data.title || "",
                    description: clubData.description || "",
                    longDescription: clubData.longDescription || "",
                    imageUrl: clubData.imageUrl || "",
                    galleryImages: clubData.galleryImages || [],
                    members: clubData.members || 0,
                    leader: clubData.leader || "",
                    contact: clubData.contact || "",
                    meetingTime: clubData.meetingTime || "",
                    meetingPlace: clubData.meetingPlace || "",
                    activities: clubData.activities || [""],
                })
            } catch (error) {
                console.error("기존 데이터 파싱 오류:", error)
            }
        }
    }, [isEdit, detail.data, hasPermission])

    // 작성 완료 후 처리
    useEffect(() => {
        if (write.success) {
            alert(isEdit ? "동아리 정보가 수정되었습니다." : "동아리가 등록되었습니다.")

            // 상태 초기
            alert(isEdit ? "동아리 정보가 수정되었습니다." : "동아리가 등록되었습니다.")

            // 상태 초기화
            dispatch({
                type: isEdit ? actionTypes.BOARD_UPDATE_RESET : actionTypes.BOARD_WRITE_RESET,
            })

            // 목록 데이터 새로고침을 위한 액션 디스패치
            dispatch({
                type: actionTypes.BOARD_FETCH,
                payload: {
                    menuId: CLUBS_MENU_ID,
                    page: 0,
                    size: 100,
                },
            })

            // 약간의 지연 후 네비게이션
            setTimeout(() => {
                navigate("/clubs", { replace: true })
            }, 100)
        }
    }, [write.success, isEdit, navigate, dispatch])

    const handleInputChange = useCallback(
        (field, value) => {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }))
            // 에러 제거
            if (errors[field]) {
                setErrors((prev) => ({
                    ...prev,
                    [field]: "",
                }))
            }
        },
        [errors],
    )

    const handleArrayChange = useCallback((field, index, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].map((item, i) => (i === index ? value : item)),
        }))
    }, [])

    const handleArrayAdd = useCallback((field, defaultValue = "") => {
        setFormData((prev) => ({
            ...prev,
            [field]: [...prev[field], defaultValue],
        }))
    }, [])

    const handleArrayRemove = useCallback((field, index) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index),
        }))
    }, [])

    const handleImageUpload = useCallback((e, isGallery = false) => {
        const file = e.target.files[0]
        if (file) {
            // 파일 크기 검증 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert("파일 크기는 5MB 이하여야 합니다.")
                return
            }

            // 파일 타입 검증
            if (!file.type.startsWith("image/")) {
                alert("이미지 파일만 업로드 가능합니다.")
                return
            }

            const reader = new FileReader()
            reader.onload = (event) => {
                const imageUrl = event.target.result
                if (isGallery) {
                    setFormData((prev) => ({
                        ...prev,
                        galleryImages: [...prev.galleryImages, imageUrl],
                    }))
                } else {
                    setFormData((prev) => ({
                        ...prev,
                        imageUrl: imageUrl,
                    }))
                }
            }
            reader.readAsDataURL(file)
        }
    }, [])

    const handleGalleryImageRemove = useCallback((index) => {
        setFormData((prev) => ({
            ...prev,
            galleryImages: prev.galleryImages.filter((_, i) => i !== index),
        }))
    }, [])

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = "동아리명을 입력해주세요."
        }

        if (!formData.description.trim()) {
            newErrors.description = "간단한 설명을 입력해주세요."
        }

        if (!formData.leader.trim()) {
            newErrors.leader = "리더명을 입력해주세요."
        }


        if (formData.members < 0) {
            newErrors.members = "회원 수는 0 이상이어야 합니다."
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // 권한 재확인
        if (!checkAdminPermission()) {
            alert("동아리 등록/수정 권한이 없습니다.")
            return
        }

        if (!validateForm()) {
            return
        }

        setIsSubmitting(true)

        try {
            // 동아리 데이터 정리
            const clubData = {
                name: formData.name,
                description: formData.description,
                longDescription: formData.longDescription,
                imageUrl: formData.imageUrl,
                galleryImages: formData.galleryImages,
                members: formData.members,
                leader: formData.leader,
                contact: formData.contact,
                email: formData.email,
                meetingTime: formData.meetingTime,
                meetingPlace: formData.meetingPlace,
                activities: formData.activities.filter((activity) => activity.trim()),
            }

            // HTML 템플릿 생성
            const htmlContent = generateClubHTML(clubData)

            let requestData

            if (isEdit) {
                // 수정 모드 - BOARD_UPDATE 액션 사용
                requestData = {
                    boardId: Number.parseInt(id), // 수정할 게시물의 ID
                    title: formData.name,
                    contents: htmlContent,
                    updateId: loginData.id || 1, // 수정하는 사용자 ID
                }


                dispatch({
                    type: actionTypes.BOARD_UPDATE,
                    payload: requestData,
                })
            } else {
                // 등록 모드 - BOARD_WRITE 액션 사용
                requestData = {
                    boardId: CLUBS_MENU_ID,
                    title: formData.name,
                    contents: htmlContent,
                    createId: loginData.id || 1,
                }


                dispatch({
                    type: actionTypes.BOARD_WRITE,
                    payload: requestData,
                })
            }
        } catch (error) {
            console.error("동아리 저장 오류:", error)
            alert("동아리 저장 중 오류가 발생했습니다.")
            setIsSubmitting(false)
        }
    }

    const handleGoBack = useCallback(() => {
        navigate(-1)
    }, [navigate])

    // 권한 체크가 완료되지 않았으면 로딩 표시
    if (!permissionChecked) {
        return (
            <div className="club-write-container">
                <div className="permission-check">
                    <h2>권한 확인 중...</h2>
                </div>
            </div>
        )
    }

    // 권한이 없으면 접근 차단
    if (!hasPermission) {
        return (
            <div className="club-write-container">
                <div className="permission-denied">
                    <h2>접근 권한이 없습니다</h2>
                    <p>동아리 등록/수정 권한이 필요합니다.</p>
                    <button onClick={handleGoBack} className="back-button">
                        돌아가기
                    </button>
                </div>
            </div>
        )
    }

    // 공통 폼 렌더링 함수
    const renderForm = (className) => (
        <div className="club-write-container">
            <div className="club-write-header">
                <button onClick={handleGoBack} className="back-button">
                    <FaArrowLeft /> 돌아가기
                </button>
                <h1 className="club-write-title">{isEdit ? "동아리 수정" : "동아리 등록"}</h1>
            </div>

            <form onSubmit={handleSubmit} className="club-write-form">
                {/* 기본 정보 섹션 */}
                <div className="form-section">
                    <h2>기본 정보</h2>
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="name">
                                동아리명 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                value={formData.name}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                placeholder="동아리명을 입력하세요"
                                className={errors.name ? "error" : ""}
                            />
                            {errors.name && <span className="error-message">{errors.name}</span>}
                        </div>


                        <div className="form-group full-width">
                            <label htmlFor="description">
                                간단한 설명 <span className="required">*</span>
                            </label>
                            <textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                placeholder="동아리에 대한 간단한 설명을 입력하세요"
                                rows="3"
                                className={errors.description ? "error" : ""}
                            />
                            {errors.description && <span className="error-message">{errors.description}</span>}
                        </div>

                        <div className="form-group full-width">
                            <label htmlFor="longDescription">상세 설명</label>
                            <textarea
                                id="longDescription"
                                value={formData.longDescription}
                                onChange={(e) => handleInputChange("longDescription", e.target.value)}
                                placeholder="동아리에 대한 상세한 설명을 입력하세요"
                                rows="5"
                            />
                        </div>
                    </div>
                </div>

                {/* 이미지 섹션 */}
                <div className="form-section">
                    <h2>이미지</h2>
                    <div className="image-upload-section">
                        <div className="main-image-upload">
                            <label>대표 이미지</label>
                            <div className="image-upload-area">
                                {formData.imageUrl ? (
                                    <div className="image-preview">
                                        <img src={formData.imageUrl || "/placeholder.svg"} alt="대표 이미지" />
                                        <button type="button" className="image-remove" onClick={() => handleInputChange("imageUrl", "")}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="image-upload-label">
                                        <FaImage />
                                        <span>대표 이미지 업로드</span>
                                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, false)} />
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="gallery-upload">
                            <label>갤러리 이미지</label>
                            <div className="gallery-grid">
                                {formData.galleryImages.map((image, index) => (
                                    <div key={index} className="gallery-item">
                                        <img src={image || "/placeholder.svg"} alt={`갤러리 ${index + 1}`} />
                                        <button type="button" className="image-remove" onClick={() => handleGalleryImageRemove(index)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                                <label className="gallery-upload-label">
                                    <FaPlus />
                                    <span>이미지 추가</span>
                                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, true)} />
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 상세 정보 섹션 */}
                <div className="form-section">
                    <h2>상세 정보</h2>
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="members">
                                <FaUsers /> 회원 수
                            </label>
                            <input
                                type="number"
                                id="members"
                                value={formData.members}
                                onChange={(e) => handleInputChange("members", Number.parseInt(e.target.value) || 0)}
                                min="0"
                                className={errors.members ? "error" : ""}
                            />
                            {errors.members && <span className="error-message">{errors.members}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="leader">
                                리더명 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="leader"
                                value={formData.leader}
                                onChange={(e) => handleInputChange("leader", e.target.value)}
                                placeholder="리더명을 입력하세요"
                                className={errors.leader ? "error" : ""}
                            />
                            {errors.leader && <span className="error-message">{errors.leader}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="meetingTime">
                                <FaCalendarAlt /> 모임 시간
                            </label>
                            <input
                                type="text"
                                id="meetingTime"
                                value={formData.meetingTime}
                                onChange={(e) => handleInputChange("meetingTime", e.target.value)}
                                placeholder="매주 주일 오후 3시"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="meetingPlace">
                                <FaMapMarkerAlt /> 모임 장소
                            </label>
                            <input
                                type="text"
                                id="meetingPlace"
                                value={formData.meetingPlace}
                                onChange={(e) => handleInputChange("meetingPlace", e.target.value)}
                                placeholder="교회 2층 본당"
                            />
                        </div>
                    </div>
                </div>

                {/* 활동 내용 섹션 */}
                <div className="form-section">
                    <h2>활동 내용</h2>
                    <div className="dynamic-list">
                        {formData.activities.map((activity, index) => (
                            <div key={index} className="dynamic-item">
                                <input
                                    type="text"
                                    value={activity}
                                    onChange={(e) => handleArrayChange("activities", index, e.target.value)}
                                    placeholder="활동 내용을 입력하세요"
                                />
                                {formData.activities.length > 1 && (
                                    <button type="button" onClick={() => handleArrayRemove("activities", index)} className="remove-btn">
                                        <FaTrash />
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => handleArrayAdd("activities")} className="add-btn">
                            <FaPlus /> 활동 추가
                        </button>
                    </div>
                </div>

                {/* 제출 버튼 */}
                <div className="form-actions">
                    <button type="button" onClick={handleGoBack} className="cancel-btn">
                        취소
                    </button>
                    <button type="submit" disabled={isSubmitting || write.loading} className="submit-btn">
                        <FaSave />
                        {isSubmitting || write.loading ? "저장 중..." : isEdit ? "수정하기" : "등록하기"}
                    </button>
                </div>

                {write.error && <div className="error-message">{write.error}</div>}
            </form>
        </div>
    )

    return (
        <>
            <PC>
                <section className="pf-page">{renderForm("pf-page")}</section>
            </PC>

            <Notebook>
                <section className="pf-page">{renderForm("pf-page")}</section>
            </Notebook>

            <Tablet>
                <section className="pf-page">{renderForm("pf-page")}</section>
            </Tablet>

            <Mobile>
                <section className="mf-page">{renderForm("mf-page")}</section>
            </Mobile>
        </>
    )
}

const mapStateToProps = (state) => ({
    loginInfo: state.user.loginInfo, // 예: store의 user slice에서 loginInfo
})

export default connect(mapStateToProps)(ClubWrite)
